import { useState, useEffect, useCallback, useRef } from "react";
import { useProject, useNotification, useApiResponseParser } from "../../../../hooks";
import { useInspectionAttributes } from "../../../../hooks/query";
import { debounce } from "lodash";

export interface GutterScopeState {
    removeExistingGutters: boolean;
    installNewGutters: boolean;
    gutterSize: string;
    gutterColor: string;
    removeExistingDownspouts: boolean;
    installNewDownspouts: boolean;
    downspoutSize: string;
    downspoutColor: string;
    gutterLength: string;
    downspoutLength: string;
}

const ATTRIBUTE_NAMES: Record<keyof GutterScopeState, string | null> = {
    removeExistingGutters: "Remove Existing Gutters",
    installNewGutters: "Install New Gutters",
    gutterColor: "Gutter Color",
    removeExistingDownspouts: "Remove Existing Downspouts",
    installNewDownspouts: "Install New Downspouts",
    downspoutColor: "Downspout Color",
    gutterSize: "Gutter Size",
    downspoutSize: "Downspout Size",
    gutterLength: "Gutter Length",
    downspoutLength: "Downspout Length",
};

const INITIAL_STATE: GutterScopeState = {
    removeExistingGutters: false,
    installNewGutters: false,
    gutterSize: "",
    gutterColor: "",
    removeExistingDownspouts: false,
    installNewDownspouts: false,
    downspoutSize: "",
    downspoutColor: "",
    gutterLength: "",
    downspoutLength: "",
};

export function useGutter() {
    const { project } = useProject();
    const { showError } = useNotification();
    const { parseErrorResponseData } = useApiResponseParser();
    const { useCreate, useListByProjectId, useUpdateSingleAttribute } = useInspectionAttributes();

    const createSvc = useCreate();
    const listSvc = useListByProjectId();
    const updateSvc = useUpdateSingleAttribute();

    const [state, setState] = useState<GutterScopeState>(INITIAL_STATE);
    const [isLoading, setIsLoading] = useState(true);
    const attributeIds = useRef<Record<string, string>>({});

    const load = async () => {
        try {
            if (!project?.id) return;

            const attrs = await listSvc.mutateAsync({ ProjectId: project.id });
            const loaded: Partial<GutterScopeState> = {};

            attrs.forEach((attr: any) => {
                // Find matching state key
                const stateKey = Object.keys(ATTRIBUTE_NAMES).find(
                    (key) => ATTRIBUTE_NAMES[key as keyof GutterScopeState] === attr.AttributeName
                ) as keyof GutterScopeState | undefined;

                if (!stateKey) return;

                attributeIds.current[attr.AttributeName] = attr.Id;
                const value = attr.AttributeValue;

                // Decide parsing based on initial state type (boolean vs string)
                const isBooleanField = typeof INITIAL_STATE[stateKey] === "boolean";
                if (isBooleanField) {
                    (loaded as any)[stateKey] = value === "true" || value === true;
                } else {
                    (loaded as any)[stateKey] = value || "";
                }
            });

            setState((prev) => ({ ...prev, ...loaded }));
        } catch (error: any) {
            console.error("Error loading gutter attributes:", error);
            const errorMessage =
                parseErrorResponseData(error) || "Failed to load gutter attributes. Please refresh the page.";
            showError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // Save attribute immediately (for checkboxes)
    const saveAttribute = useCallback(
        async (attributeName: string, value: string | boolean) => {
            if (!project?.id) return;

            const stringValue = String(value);
            const existingId = attributeIds.current[attributeName];

            try {
                if (existingId) {
                    // Update existing
                    await updateSvc.mutateAsync({ Id: existingId, Value: stringValue });
                } else {
                    // Attribute should exist from project creation, but if not, fetch and update
                    const attrs = await listSvc.mutateAsync({ ProjectId: project.id });
                    const existing = attrs.find((attr: any) => attr.AttributeName === attributeName);
                    if (existing) {
                        await updateSvc.mutateAsync({ Id: existing.Id, Value: stringValue });
                        attributeIds.current[attributeName] = existing.Id;
                    } else {
                        // Fallback: create if somehow missing (shouldn't happen)
                        await createSvc.mutateAsync({
                            ProjectId: project.id,
                            [attributeName]: stringValue,
                        });
                        const updated = await listSvc.mutateAsync({ ProjectId: project.id });
                        const newAttr = updated.find((attr: any) => attr.AttributeName === attributeName);
                        if (newAttr) {
                            attributeIds.current[attributeName] = newAttr.Id;
                        }
                    }
                }
            } catch (error: any) {
                console.error(`Error saving ${attributeName}:`, error);
                const errorMessage =
                    parseErrorResponseData(error) || `Failed to save ${attributeName}. Please try again.`;
                showError(errorMessage);
            }
        },
        [project?.id, createSvc, updateSvc, listSvc]
    );

    // Debounced save for text inputs only
    const debouncedSave = useRef(debounce(saveAttribute, 500)).current;

    // Update state and save
    const updateGutterState = useCallback(
        <K extends keyof GutterScopeState>(key: K, value: GutterScopeState[K]) => {
            setState((prev) => ({ ...prev, [key]: value }));

            const attributeName = ATTRIBUTE_NAMES[key];
            if (!attributeName) return;

            // Save immediately for booleans, debounced for strings (colors)
            if (typeof value === "boolean") {
                saveAttribute(attributeName, value);
            } else {
                debouncedSave(attributeName, value);
            }
        },
        [saveAttribute, debouncedSave]
    );

    // Load existing attributes
    useEffect(() => {
        if (!project?.id) {
            setIsLoading(false);
            return;
        }
        load();
    }, [project?.id]);

    return {
        state,
        updateGutterState,
        isLoading,
    };
}
